import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, BookOpen } from "lucide-react";
import Seo, { absUrl } from "@/components/Seo";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Footer";
import { Reveal } from "../components/landing/Reveal";
import { optImg } from "../lib/img";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const BlogPage = () => {
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/posts`).then(({ data }) => setPosts(data)).catch(() => {});
    const lenis = window.__lenis;
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, []);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Voyage Switzerland Travel Guide",
    url: `${window.location.origin}/blog`,
    description: "Local guides to private tours, destinations and luxury travel in Switzerland.",
    publisher: {
      "@type": "Organization",
      name: "Voyage Switzerland",
      url: window.location.origin,
      logo: { "@type": "ImageObject", url: absUrl("/logo.png") },
    },
  };

  return (
    <div data-testid="blog-page">
      <Seo
        title="Switzerland Travel Guide — Tips, Itineraries & Local Advice | Voyage Switzerland"
        siteName="Voyage Switzerland"
        description="Local guides to private tours, destinations and luxury travel in Switzerland — written by the Voyage Switzerland guide team."
        image="/og-image.png"
        jsonLd={jsonLd}
        alternates={{ en: "/blog" }}
        lang="en"
      />
      <Navbar />
      <section className="pt-[110px] sm:pt-[130px] pb-24 bg-[#f8fafc] min-h-screen">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <p className="text-xs font-bold uppercase tracking-widest text-[var(--brand)]">Travel Guide</p>
            <h1 className="mt-3 font-display font-bold tracking-tight text-4xl sm:text-5xl text-[#0b132b]" data-testid="blog-title">
              Switzerland, Explained by Locals
            </h1>
            <p className="mt-4 max-w-2xl text-base sm:text-lg text-slate-600 leading-relaxed">
              Honest advice from twenty years of guiding — the best private tours, destinations, itineraries and alpine experiences.
            </p>
          </Reveal>

          {posts.length === 0 ? (
            <div className="mt-16 text-center text-slate-400" data-testid="blog-empty">
              <BookOpen className="w-10 h-10 mx-auto mb-3 text-slate-300" />
              Articles are on their way.
            </div>
          ) : (
            <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
              {posts.map((p, i) => (
                <Reveal key={p.id} delay={(i % 3) * 0.08}>
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`blog-card-${p.slug}`}
                    className="group flex flex-col h-full rounded-3xl bg-white border border-slate-100 overflow-hidden shadow-[0_16px_40px_-20px_rgba(11,19,43,0.15)] hover:shadow-[0_30px_60px_-24px_rgba(225,29,72,0.35)] hover:border-[var(--brand)]/25 transition-all duration-300 hover:-translate-y-1.5"
                  >
                    {p.image && (
                      <div className="h-44 overflow-hidden">
                        <img src={optImg(p.image, 800)} alt={p.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                      </div>
                    )}
                    <div className="p-6 flex flex-col flex-1">
                      <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">
                        {new Date(p.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" })}
                      </p>
                      <h2 className="mt-2 font-display font-bold text-lg text-[#0b132b] leading-tight group-hover:text-[var(--brand)] transition-colors">
                        {p.title}
                      </h2>
                      <p className="mt-2 text-sm text-slate-600 leading-relaxed flex-1">{p.excerpt}</p>
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--brand)] group-hover:gap-2.5 transition-all">
                        Read the guide <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};
